import { sessionService } from '../services/sessionService.js';

// Pure API-based session manager - No localStorage usage
export class ApiSessionManager {
  constructor() {
    this.sessionCache = new Map(); // In-memory cache for performance
    this.lastTokenUsed = null;
  }

  // Get authentication token
  async getAuthToken(instance, accounts, loginRequest) {
    if (!accounts || accounts.length === 0) {
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      this.lastTokenUsed = response.accessToken;
      return response.accessToken;
    } catch (error) {
      console.warn('Failed to acquire token:', error);
      return null;
    }
  }  // Get all sessions from API only
  async getSessions(token) {
    try {
      const sessions = await sessionService.getSessions(token);
      
      // Cache sessions for performance
      sessions.forEach(session => {
        this.sessionCache.set(session.id, session);
      });
      
      return sessions;
    } catch (error) {
      console.error('Failed to load sessions from API:', error);
      return [];
    }
  }  // Get session details with caching
  async getSessionDetails(sessionId, token) {
    // Check cache first
    const cached = this.sessionCache.get(sessionId);
    if (cached && cached.messages && cached.messages.length > 0) {
      return cached;
    }

    try {
      const sessionDetails = await sessionService.getSessionDetails(sessionId, token);
      
      if (sessionDetails) {
        // Update cache
        this.sessionCache.set(sessionId, sessionDetails);
        return sessionDetails;
      }
      
      return null;
    } catch (error) {
      console.error(`Failed to load session details for ${sessionId}:`, error);
      
      // Check if it's a 500 error or other server error
      if (error.message.includes('500') || 
          error.message.includes('Internal Server Error') || 
          error.message.includes('Internal server error')) {
        console.warn(`Session ${sessionId} has server error, marking as failed`);
        // Return a special error marker so the caller knows this session failed
        throw new Error(`SESSION_ERROR: Server error for session ${sessionId}`);
      }
      
      return null;
    }
  }

  // Create new session (temporary until backend supports it)
  createTempSession(userName = '') {
    const tempId = `temp-${Date.now()}`;
    const newSession = {
      id: tempId,
      title: 'New Conversation',
      messages: [],
      createdBy: userName,
      createdAt: new Date().toISOString(),
      lastModified: null, // New sessions haven't been modified yet
      isTemporary: true
    };

    // Cache the temp session
    this.sessionCache.set(tempId, newSession);
    return newSession;
  }

  // Update session in cache
  updateSessionInCache(sessionId, updates) {
    const cached = this.sessionCache.get(sessionId);
    if (cached) {
      // Only update lastModified if the session is actually being modified (e.g., messages added)
      const shouldUpdateLastModified = updates.messages || updates.title || updates.session_name;
      const updated = { 
        ...cached, 
        ...updates,
        ...(shouldUpdateLastModified && { lastModified: new Date().toISOString() })
      };
      this.sessionCache.set(sessionId, updated);
      return updated;
    }
    return null;
  }

  // Add message to session in cache
  addMessageToSession(sessionId, message) {
    const session = this.sessionCache.get(sessionId);
    if (session) {
      const updatedSession = {
        ...session,
        messages: [...(session.messages || []), message],
        lastModified: new Date().toISOString() // Update lastModified when messages are added
      };
      this.sessionCache.set(sessionId, updatedSession);
      return updatedSession;
    }
    return null;
  }

  // Update message in session cache
  updateMessageInSession(sessionId, messageId, updates) {
    const session = this.sessionCache.get(sessionId);
    if (session && session.messages) {
      const messageIndex = session.messages.findIndex(m => m.id === messageId);
      if (messageIndex >= 0) {
        const updatedMessages = [...session.messages];
        updatedMessages[messageIndex] = { ...updatedMessages[messageIndex], ...updates };
        
        const updatedSession = {
          ...session,
          messages: updatedMessages,
          lastModified: new Date().toISOString() // Update lastModified when messages are updated
        };
        
        this.sessionCache.set(sessionId, updatedSession);
        return updatedSession;
      }
    }
    return null;
  }

  // Update session title when backend provides it
  updateSessionTitle(sessionId, title) {
    return this.updateSessionInCache(sessionId, { title });
  }

  // Replace temp session with real session ID from backend
  replaceSessionId(tempId, realId, sessionName = null) {
    const tempSession = this.sessionCache.get(tempId);
    if (tempSession) {
      const updatedSession = {
        ...tempSession,
        id: realId,
        title: sessionName || tempSession.title,
        isTemporary: false,
        isFromAPI: true
      };
      
      // Remove temp session and add real session
      this.sessionCache.delete(tempId);
      this.sessionCache.set(realId, updatedSession);
      
      return updatedSession;
    }
    return null;
  }

  // Get session from cache
  getSessionFromCache(sessionId) {
    return this.sessionCache.get(sessionId);
  }

  // Clear cache (useful for sign out)
  clearCache() {
    this.sessionCache.clear();
    this.lastTokenUsed = null;
    console.log('Session cache cleared');
  }

  // Get all cached sessions
  getAllCachedSessions() {
    return Array.from(this.sessionCache.values())
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  // Check if session exists in cache
  hasSessionInCache(sessionId) {
    return this.sessionCache.has(sessionId);
  }

  // Remove session from cache
  removeSessionFromCache(sessionId) {
    return this.sessionCache.delete(sessionId);
  }

  // Create new session via API
  async createSession(title, token) {
    try {
      const response = await sessionService.createSession(title, token);
      
      if (response && response.session_id) {
        const newSession = {
          id: response.session_id,
          title: response.session_name || title || 'New Conversation',
          messages: [],
          createdBy: '',
          createdAt: new Date().toISOString(),
          isFromAPI: true
        };
        
        // Cache the new session
        this.sessionCache.set(newSession.id, newSession);
        return newSession;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }

  // Update session via API
  async updateSession(sessionId, updates, token) {
    try {
      const response = await sessionService.updateSession(sessionId, updates, token);
      
      // Update cache
      this.updateSessionInCache(sessionId, updates);
      
      return response;
    } catch (error) {
      console.error(`Failed to update session ${sessionId}:`, error);
      throw error;
    }
  }

  // Delete session via API
  async deleteSession(sessionId, token) {
    try {
      const response = await sessionService.deleteSession(sessionId, token);
      
      // Remove from cache
      this.removeSessionFromCache(sessionId);
      
      return response;
    } catch (error) {
      console.error(`Failed to delete session ${sessionId}:`, error);
      throw error;
    }
  }
}

// Create a singleton instance
export const apiSessionManager = new ApiSessionManager();

export default apiSessionManager;
